import { useEffect, useState } from "react";
import { ChangePasswordButton, ChangePasswordDialog } from "@/components/ChangePassword";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { AgyinPanel } from "@/components/agyin/AgyinPanel";
import { AdminAgyinPanel } from "@/components/agyin/AdminAgyinPanel";
import { recordActivity } from "@/lib/activity";
import { LogOut, Menu, X, AlertTriangle, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { BranchSelector } from "@/components/BranchSelector";
import { NotificationBell } from "@/components/NotificationBell";
import { api } from "@/lib/api";
import { OpsSessionBanner } from "@/components/OpsSessionBanner";

function Banner({ mode, isAdmin }) {
  if (mode === "full") return null;
  const locked = mode === "locked";
  return (
    <div data-testid="access-mode-banner" className={cn("flex items-center gap-3 px-6 py-2.5 text-sm border-b", locked ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-orange-50 text-orange-900 border-orange-200")}>
      {locked ? <Lock className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
      <span className="flex-1">
        {locked ? "Subscription inactive — workspace is locked. Only the admin can sign in to reactivate." : "Subscription payment overdue — workspace is read-only until billing is resolved."}
      </span>
      {isAdmin && <Link to="/app/billing" data-testid="banner-billing-link" className="font-semibold underline underline-offset-2">Resolve billing</Link>}
    </div>
  );
}

function SideNav({ nav, onNavigate }) {
  const groups = nav.reduce((acc, n) => {
    const last = acc[acc.length - 1];
    if (last && last.section === (n.section || null)) last.items.push(n);
    else acc.push({ section: n.section || null, items: [n] });
    return acc;
  }, []);
  return (
    <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4" data-testid="sidebar-nav">
      {groups.map((g, gi) => (
        <div key={`${g.section}-${gi}`} className={cn("space-y-0.5", gi > 0 && "mt-6")} data-testid={g.section ? `nav-section-${g.section.toLowerCase().replace(/\s+/g, "-")}` : undefined}>
          {g.section && <div className="po-nav-section">{g.section}</div>}
          {g.items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.id}`} className="po-nav-item">
              <n.icon />
              <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function ActivityTracker() {
  const loc = useLocation();
  useEffect(() => { recordActivity({ type: "visit", route: loc.pathname + loc.search }); }, [loc.pathname, loc.search]);
  return null;
}

export default function AppShell({ nav, contextLabel }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const [bkey, setBkey] = useState(0);
  const mode = user?.tenant?.access_mode || "full";
  const doLogout = async () => { await logout(); navigate("/login"); };

  const aside = (
    <aside className="po-sidebar flex h-full w-64 flex-col">
      <div className="shrink-0 px-4 pt-5 pb-4" style={{ borderBottom: "1px solid var(--po-sidebar-border)" }}>
        <div className="rounded-lg bg-white px-3 py-2"><img src="/brand/logo.png" alt="PressOps" className="h-8 w-auto object-contain object-left" /></div>
        <div className="mt-4 px-1 text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: "var(--po-sidebar-muted)" }}>{contextLabel}</div>
        <div className="mt-1 truncate px-1 text-sm font-semibold text-white" data-testid="sidebar-context-name">{user?.tenant?.name || "Platform administration"}</div>
      </div>
      <SideNav nav={nav} onNavigate={() => setOpen(false)} />
      <div className="shrink-0 p-4" style={{ borderTop: "1px solid var(--po-sidebar-border)" }} data-testid="sidebar-footer">
        <div className="truncate text-sm font-medium text-white" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="truncate text-xs text-slate-400" data-testid="sidebar-user-email">{user?.email}</div>
        <div className="mt-1.5 inline-flex rounded-full bg-white/10 px-2 py-0.5 text-[11px] capitalize text-slate-300" data-testid="sidebar-user-role">{user?.role?.replace("_", " ")}</div>
        {!user?.impersonating && !user?.ops_session && <ChangePasswordButton className="po-nav-item mt-3 w-full !px-2" onClick={() => { setOpen(false); setPwOpen(true); }} />}
        <button onClick={doLogout} data-testid="logout-button" className="po-nav-item mt-1 w-full !px-2">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <div className="sticky top-0 hidden h-screen md:block">{aside}</div>
      {open && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="h-full">{aside}</div>
          <button className="flex-1 bg-black/40" onClick={() => setOpen(false)} data-testid="mobile-nav-close" aria-label="Close navigation"><X className="ml-auto mr-4 mt-4 h-6 w-6 text-white" /></button>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur-md md:hidden">
          <button onClick={() => setOpen(true)} data-testid="mobile-nav-open" aria-label="Open navigation" className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-md hover:bg-slate-100"><Menu className="h-5 w-5" /></button>
          <img src="/brand/logo.png" alt="PressOps" className="h-7" />
          {user?.tenant && <div className="ml-auto"><NotificationBell client={api} testId="staff-bell-mobile" /></div>}
        </header>
        <Banner mode={mode} isAdmin={user?.role === "tenant_admin" && !user?.impersonating} />
        {user?.ops_session && <OpsSessionBanner s={user.ops_session} />}
        {user?.impersonating && (
          <div data-testid="impersonation-banner" className="flex items-center gap-3 border-b border-indigo-200 bg-indigo-50 px-6 py-2.5 text-sm text-indigo-900">
            <span className="flex-1">Read-only support view of <b>{user?.tenant?.name}</b>. Changes are blocked and this session is audit-logged.</span>
            <button data-testid="exit-impersonation-button" className="font-semibold underline" onClick={() => {
              localStorage.setItem("pressops_token", localStorage.getItem("pressops_platform_token") || "");
              localStorage.removeItem("pressops_platform_token"); localStorage.removeItem("pressops_user");
              window.location.href = "/platform";
            }}>Exit support view</button>
          </div>
        )}
        <main className="mx-auto w-full max-w-7xl space-y-6 p-4 pb-24 sm:p-8" key={bkey}>
          {user?.tenant && !user?.impersonating && <div className="flex items-center justify-end gap-2"><span className="hidden md:block"><NotificationBell client={api} testId="staff-bell" /></span><BranchSelector onChange={() => setBkey((k) => k + 1)} /></div>}
          <Outlet />
        </main>
        {user?.tenant && !user?.impersonating && user.tenant.capabilities?.includes("ai_assist") && <AgyinPanel />}
        {!user?.tenant && !user?.ops_session && ["platform_admin", "platform_ops"].includes(user?.role) && <AdminAgyinPanel />}
        <ActivityTracker />
      </div>
    </div>
  );
}
